// VisitMediaInline — Quick win #7 (May 2, 2026)
//
// Renders inline voice + photo for a canvas_visits row inside the knock-
// history list. Resolves signed URLs from the deal-documents bucket on
// demand and caches them per-component so each row only fetches once.
//
// Voice: small <audio controls>, ~24px tall.
// Photo: 32×32 thumbnail, tap → opens full-size in new tab via fresh
// signed URL (since the cached URL may have expired by tap time).

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";

const SIGNED_URL_TTL = 3600; // 1 hour

async function signedUrl(path: string): Promise<string | null> {
  if (!path) return null;
  // Strip any leading "/" to be safe
  const clean = path.replace(/^\/+/, "");
  const { data, error } = await supabase.storage
    .from("deal-documents")
    .createSignedUrl(clean, SIGNED_URL_TTL);
  if (error) return null;
  return data?.signedUrl ?? null;
}

export default function VisitMediaInline({
  voicePath,
  photoPath,
}: {
  voicePath: string | null;
  photoPath: string | null;
}) {
  const [voiceUrl, setVoiceUrl] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (voicePath) {
      signedUrl(voicePath).then((u) => { if (!cancelled) setVoiceUrl(u); });
    } else {
      setVoiceUrl(null);
    }
    if (photoPath) {
      signedUrl(photoPath).then((u) => { if (!cancelled) setPhotoUrl(u); });
    } else {
      setPhotoUrl(null);
    }
    return () => { cancelled = true; };
  }, [voicePath, photoPath]);

  async function openPhotoFull() {
    if (!photoPath) return;
    // Generate a fresh URL on tap so we don't fail if the thumbnail URL has aged out
    const u = await signedUrl(photoPath);
    if (u) window.open(u, "_blank", "noopener,noreferrer");
  }

  if (!voicePath && !photoPath) return null;

  return (
    <div className="mt-1 flex items-center gap-2">
      {voicePath && voiceUrl ? (
        <audio
          controls
          src={voiceUrl}
          className="h-6 max-w-[180px]"
          preload="metadata"
        />
      ) : voicePath ? (
        <span className="rounded bg-purple-50 px-1.5 py-0.5 text-[9px] font-semibold text-purple-700">🎤 Loading…</span>
      ) : null}

      {photoPath && photoUrl ? (
        <button
          type="button"
          onClick={openPhotoFull}
          className="flex h-8 w-8 items-center justify-center overflow-hidden rounded border border-emerald-200 bg-white"
          title="Open photo"
        >
          <img src={photoUrl} alt="" className="h-full w-full object-cover" />
        </button>
      ) : photoPath ? (
        <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">📷 Loading…</span>
      ) : null}
    </div>
  );
}
